import type { Href } from "expo-router";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import { ActivityIndicator, Platform, Pressable, ScrollView, Text, View } from "react-native";

import { getStudentHistory, getStudentProfile } from "../../../../lib/studentProfile";
import type {
  StudentCompetitionHistoryRow,
  StudentProfileFull,
} from "../../../../types/database";
import { FONTS } from "../../../../lib/brand";
import { PANE_THEMES } from "../../../../components/brand/paneThemes";

const THEME = PANE_THEMES.Judge;
const FOREST_MID = THEME.surfaceBg;
const AMBER = THEME.accent;
const SAGE = THEME.success;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const RED = THEME.destructive;

const PAGE_MAX_W = 980;

function makeInitials(s: string | null | undefined): string {
  if (!s?.trim()) return "??";
  const parts = s.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function classLine(profile: StudentProfileFull): string {
  const parts: string[] = [];
  if (profile.year_in_school) {
    parts.push(profile.year_in_school[0].toUpperCase() + profile.year_in_school.slice(1));
  }
  if (profile.graduation_term && profile.graduation_year) {
    parts.push(`${profile.graduation_term} ${profile.graduation_year}`);
  } else if (profile.graduation_year) {
    parts.push(String(profile.graduation_year));
  }
  return parts.join(" · ");
}

export default function JudgeStudentProfileScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const studentId = typeof id === "string" ? id : "";
  const [profile, setProfile] = useState<StudentProfileFull | null>(null);
  const [history, setHistory] = useState<StudentCompetitionHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!studentId) return;
      setLoading(true);
      setErr(null);
      try {
        const [p, h] = await Promise.all([
          getStudentProfile(studentId),
          getStudentHistory(studentId),
        ]);
        if (cancelled) return;
        if (!p) {
          setErr("This candidate's profile is unavailable.");
        }
        setProfile(p);
        setHistory(h);
      } catch (e) {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : "Could not load the candidate profile.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  if (loading) {
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
        <ActivityIndicator size="small" color={AMBER} />
      </View>
    );
  }

  if (err || !profile) {
    return (
      <View style={{ flex: 1, padding: 32 }}>
        <BackLink />
        <Text style={{ marginTop: 16, fontFamily: FONTS.bodyMedium, fontSize: 13, color: RED }}>
          {err ?? "Profile unavailable."}
        </Text>
      </View>
    );
  }

  const cline = classLine(profile);

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 80 }}>
      <View style={{ paddingHorizontal: 32, paddingTop: 28, maxWidth: PAGE_MAX_W }}>
        <BackLink />

        {/* Hero */}
        <View style={{ marginTop: 16, flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 10,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Candidate context
          </Text>
        </View>

        <View style={{ marginTop: 14, flexDirection: "row", alignItems: "center", gap: 16 }}>
          <View
            style={{
              width: 64,
              height: 64,
              borderRadius: 999,
              backgroundColor: "rgba(181,135,58,0.16)",
              borderWidth: 1,
              borderColor: "rgba(181,135,58,0.30)",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.displaySemiBold,
                fontSize: 22,
                color: AMBER,
              }}
            >
              {makeInitials(profile.full_name)}
            </Text>
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              style={{
                fontFamily: FONTS.displayMedium,
                fontSize: 30,
                lineHeight: 34,
                color: LINEN,
              }}
              numberOfLines={2}
            >
              {profile.full_name ?? "Candidate"}
            </Text>
            <Text
              style={{
                marginTop: 4,
                fontFamily: FONTS.body,
                fontSize: 13,
                color: WS,
              }}
            >
              {[profile.university, profile.major].filter(Boolean).join(" · ") || "—"}
              {cline ? ` · ${cline}` : ""}
            </Text>
          </View>
        </View>

        {/* Disclosure: contact info hidden for judges */}
        <View
          style={{
            marginTop: 18,
            paddingHorizontal: 12,
            paddingVertical: 10,
            borderRadius: 6,
            borderWidth: 1,
            borderColor: BORDER,
            backgroundColor: "rgba(255,255,255,0.02)",
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 11.5,
              lineHeight: 17,
              color: WL,
            }}
          >
            Judges see academic context only. Contact info, resume, and recruiting actions are reserved for the inviting client.
          </Text>
        </View>

        {/* About / bio */}
        <Section title="About">
          {profile.bio?.trim() ? (
            <Text
              style={{
                fontFamily: FONTS.bodyLight,
                fontSize: 13,
                lineHeight: 21,
                color: WS,
              }}
            >
              {profile.bio}
            </Text>
          ) : (
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 12,
                fontStyle: "italic",
                color: WL,
              }}
            >
              {(profile.full_name?.split(" ")[0] ?? "They")} hasn&apos;t added a bio yet.
            </Text>
          )}
        </Section>

        {/* Fields of competency */}
        <Section title="Fields of competency">
          {profile.fields_of_competency && profile.fields_of_competency.length > 0 ? (
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
              {profile.fields_of_competency.map((f) => (
                <View
                  key={f}
                  style={{
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                    borderRadius: 999,
                    backgroundColor: "rgba(143,187,138,0.10)",
                    borderWidth: 1,
                    borderColor: "rgba(143,187,138,0.35)",
                  }}
                >
                  <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: SAGE }}>{f}</Text>
                </View>
              ))}
            </View>
          ) : (
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 12,
                fontStyle: "italic",
                color: WL,
              }}
            >
              Not specified yet.
            </Text>
          )}
        </Section>

        {/* Competition history */}
        <Section title="Competition history">
          {history.length === 0 ? (
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 12,
                fontStyle: "italic",
                color: WL,
              }}
            >
              No prior Coravu cases.
            </Text>
          ) : (
            <View style={{ gap: 8 }}>
              {history.map((row) => (
                <View
                  key={`${row.competition_id}_${row.team_id}`}
                  style={{
                    padding: 14,
                    borderRadius: 6,
                    backgroundColor: FOREST_MID,
                    borderWidth: 1,
                    borderColor: BORDER,
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      style={{
                        fontFamily: FONTS.displayMedium,
                        fontSize: 16,
                        color: LINEN,
                      }}
                      numberOfLines={1}
                    >
                      {row.competition_name}
                    </Text>
                    <Text
                      style={{
                        marginTop: 2,
                        fontFamily: FONTS.body,
                        fontSize: 11.5,
                        color: WM,
                      }}
                      numberOfLines={1}
                    >
                      {row.company_name} · Team {row.team_name}
                    </Text>
                  </View>
                  {row.placement != null ? (
                    <View
                      style={{
                        paddingHorizontal: 10,
                        paddingVertical: 4,
                        borderRadius: 999,
                        backgroundColor: "rgba(143,187,138,0.18)",
                      }}
                    >
                      <Text
                        style={{
                          fontFamily: FONTS.bodyBold,
                          fontSize: 10,
                          letterSpacing: 1.2,
                          color: SAGE,
                        }}
                      >
                        Placed #{row.placement}
                      </Text>
                    </View>
                  ) : (
                    <View
                      style={{
                        paddingHorizontal: 10,
                        paddingVertical: 4,
                        borderRadius: 999,
                        backgroundColor: "rgba(255,255,255,0.04)",
                      }}
                    >
                      <Text
                        style={{
                          fontFamily: FONTS.bodyBold,
                          fontSize: 10,
                          letterSpacing: 1.2,
                          color: WM,
                        }}
                      >
                        {row.competition_status}
                      </Text>
                    </View>
                  )}
                </View>
              ))}
            </View>
          )}
        </Section>
      </View>
    </ScrollView>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={{ marginTop: 26 }}>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: AMBER,
          marginBottom: 10,
        }}
      >
        {title}
      </Text>
      {children}
    </View>
  );
}

function BackLink() {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null;
  return (
    <Pressable
      onPress={() => {
        if (router.canGoBack()) router.back();
        else router.replace("/judge" as Href);
      }}
      style={[cursorWeb, { alignSelf: "flex-start", paddingVertical: 4 }]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyMedium,
          fontSize: 11.5,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: WM,
        }}
      >
        {"← Back"}
      </Text>
    </Pressable>
  );
}

// Silence unused-import warnings for tokens we don't render.
void W;
